import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import Button from '@/components/base/Button';
import EmptyState from '@/components/base/EmptyState';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import Toast, { type ToastType } from '@/components/base/Toast';
import PackageCard from '@/pages/packages/components/PackageCard';
import { useAppData } from '@/store/AppDataContext';

type FilterState = 'all' | 'active' | 'disabled';

interface ToastState {
  type: ToastType;
  message: string;
}

export default function PackagesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { packages, togglePackageActive, deletePackage } = useAppData();

  const [filter, setFilter] = useState<FilterState>('all');
  const [deleteId, setDeleteId] = useState('');
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    const state = location.state as { toast?: ToastState } | null;
    if (state?.toast) {
      setToast(state.toast);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const showToast = (type: ToastType, message: string) => setToast({ type, message });

  const filteredPackages = packages.filter((pkg) => {
    if (filter === 'active') return pkg.active;
    if (filter === 'disabled') return !pkg.active;
    return true;
  });

  const activeCount = packages.filter((p) => p.active).length;
  const disabledCount = packages.filter((p) => !p.active).length;
  const totalSubscribers = packages.reduce((acc, p) => acc + p.subscribers.length, 0);

  const handleToggleActive = (id: string) => {
    const pkg = packages.find((p) => p.id === id);
    if (!pkg) return;
    togglePackageActive(id);
    showToast('info', pkg.active ? `تم تعطيل "${pkg.name}"` : `تم تفعيل "${pkg.name}"`);
  };

  const confirmDelete = () => {
    const deleted = packages.find((pkg) => pkg.id === deleteId);
    deletePackage(deleteId);
    if (deleted) showToast('info', `تم حذف "${deleted.name}"`);
    setDeleteId('');
  };

  return (
    <DashboardLayout title="الباقات">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-bold text-[18px] text-foreground-950">الباقات</h2>
          <Button variant="cta" size="md" icon="ri-add-line" onClick={() => navigate('/packages/new')}>
            إضافة باقة جديدة
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { icon: 'ri-box-3-line', label: 'إجمالي الباقات', value: packages.length, bg: 'bg-secondary-100', text: 'text-secondary-600' },
            { icon: 'ri-checkbox-circle-line', label: 'باقات نشطة', value: activeCount, bg: 'bg-success-100', text: 'text-success-600' },
            { icon: 'ri-close-circle-line', label: 'باقات معطّلة', value: disabledCount, bg: 'bg-danger-100', text: 'text-danger-600' },
            { icon: 'ri-team-line', label: 'إجمالي المشتركين', value: totalSubscribers, bg: 'bg-accent-100', text: 'text-accent-700' },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-3 rounded-card border border-background-200 bg-white p-4 shadow-card">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${stat.bg} ${stat.text}`}>
                <i className={`${stat.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-[11px] text-foreground-400">{stat.label}</p>
                <p className="mt-0.5 text-[20px] font-bold text-foreground-950">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {(['all', 'active', 'disabled'] as FilterState[]).map((f) => {
            const labels: Record<FilterState, string> = {
              all: `الكل (${packages.length})`,
              active: `نشطة (${activeCount})`,
              disabled: `معطّلة (${disabledCount})`,
            };
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={[
                  'whitespace-nowrap rounded-btn px-4 py-2 text-[13px] font-medium transition-colors',
                  filter === f
                    ? 'bg-primary-500 text-white'
                    : 'border border-background-200 bg-white text-foreground-600 hover:bg-background-50',
                ].join(' ')}
              >
                {labels[f]}
              </button>
            );
          })}
        </div>

        {filteredPackages.length === 0 ? (
          <EmptyState
            icon="ri-box-3-line"
            title="لا توجد باقات"
            message={
              filter === 'disabled'
                ? 'لا توجد باقات معطّلة في الوقت الحالي.'
                : 'ابدأ بإنشاء أول باقة اشتراك للعملاء.'
            }
            action={
              filter === 'all' ? (
                <Button variant="cta" icon="ri-add-line" onClick={() => navigate('/packages/new')}>
                  إضافة أول باقة
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {filteredPackages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                onToggleActive={() => handleToggleActive(pkg.id)}
                onEdit={() => navigate(`/packages/${pkg.id}/edit`)}
                onDelete={() => setDeleteId(pkg.id)}
                onViewSubscribers={() => navigate(`/packages/${pkg.id}/subscribers`)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleteId)}
        title="تأكيد حذف الباقة"
        message={
          <span>
            هل أنت متأكد من حذف هذه الباقة؟
            <br />
            <span className="mt-1 block text-[12px] text-foreground-400">
              المشتركون الحاليون لن يتأثروا — يظل اشتراكهم فعّالاً حتى انتهاء صلاحيته.
            </span>
          </span>
        }
        confirmLabel="حذف الباقة"
        tone="danger"
        onConfirm={confirmDelete}
        onClose={() => setDeleteId('')}
      />
    </DashboardLayout>
  );
}